/* ──────────────────────────────────────────────────────────
 *  ChatApiService
 *
 *  Talks to the Express/SQLite backend which stubs the ACS
 *  Chat REST API.  When you're ready to swap in the real
 *  @azure/communication-chat SDK, replace the HTTP calls here.
 * ────────────────────────────────────────────────────────── */

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import {
  ChatUser,
  ChatThread,
  ChatMessage,
  CreateThreadRequest,
  TypingIndicatorEvent,
} from '../models';
import { acsChatEnvironment } from '../environments/environment';

@Injectable({ providedIn: 'root' })
export class ChatApiService {
  private readonly http = inject(HttpClient);
  private readonly base = acsChatEnvironment.apiBaseUrl;

  /* ── Users ───────────────────────────────────────────── */

  getUsers(): Observable<ChatUser[]> {
    return this.http.get<ChatUser[]>(`${this.base}/users`);
  }

  getUser(id: string): Observable<ChatUser> {
    return this.http.get<ChatUser>(`${this.base}/users/${id}`);
  }

  ensureUser(id: string, displayName: string): Observable<ChatUser> {
    return this.http.post<ChatUser>(`${this.base}/users/ensure`, {
      id,
      displayName,
    });
  }

  /* ── Threads ─────────────────────────────────────────── */

  getThreads(userId: string): Observable<ChatThread[]> {
    return this.http.get<ChatThread[]>(`${this.base}/threads`, {
      params: { userId },
    });
  }

  createThread(req: CreateThreadRequest): Observable<{ threadId: string }> {
    return this.http.post<{ threadId: string }>(`${this.base}/threads`, {
      ...req,
      retentionPolicy: req.retentionPolicy ?? 'indefinite',
    });
  }

  /* ── Messages ────────────────────────────────────────── */

  getMessages(threadId: string): Observable<ChatMessage[]> {
    return this.http.get<ChatMessage[]>(
      `${this.base}/threads/${threadId}/messages`,
    );
  }

  sendMessage(
    threadId: string,
    senderId: string,
    content: string,
    type: 'text' | 'image' = 'text',
  ): Observable<ChatMessage> {
    return this.http.post<ChatMessage>(
      `${this.base}/threads/${threadId}/messages`,
      { senderId, content, type },
    );
  }

  updateMessageStatus(
    messageId: string,
    status: 'sent' | 'delivered' | 'read',
  ): Observable<{ ok: boolean }> {
    return this.http.patch<{ ok: boolean }>(
      `${this.base}/messages/${messageId}/status`,
      { status },
    );
  }

  /* ── Typing ──────────────────────────────────────────── */

  sendTypingIndicator(
    threadId: string,
    senderId: string,
    senderDisplayName: string,
  ): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(
      `${this.base}/threads/${threadId}/typing`,
      { senderId, senderDisplayName },
    );
  }

  getTypingIndicators(
    threadId: string,
    excludeUserId: string,
  ): Observable<TypingIndicatorEvent[]> {
    return this.http.get<TypingIndicatorEvent[]>(
      `${this.base}/threads/${threadId}/typing`,
      { params: { excludeUserId } },
    );
  }
}
